import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { memo, useCallback, useState, type ElementType } from 'react'
import { useTranslation } from '@spcc/core'
import {
  ArrowLeft,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Scan,
  LayoutGrid,
  ShoppingCart,
  Truck,
  cn,
} from '@spcc/uikit'
import { getPortalUrl } from '@spcc/portal-shared'
import { selectIsAuthenticated } from '@spcc/domains/auth'
import type { RouterContext } from '@/main'

type HomeStep = 'choose' | 'existing'

interface ActionCardProps {
  onClick: () => void
  title: string
  description: string
  icon: ElementType
  iconClassName?: string
  iconContainerClassName: string
  borderClassName: string
  gradientClassName: string
}

function getActionCardClassName(borderClassName: string): string {
  return cn(
    'group relative overflow-hidden border-2 cursor-pointer bg-card/90',
    'transform-gpu transition-[transform,border-color,box-shadow] duration-200 ease-out',
    'will-change-transform hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-xl',
    borderClassName
  )
}

function getActionCardOverlayClassName(gradientClassName: string): string {
  return cn(
    'pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100',
    gradientClassName
  )
}

function getActionCardIconContainerClassName(iconContainerClassName: string): string {
  return cn(
    'w-24 h-24 rounded-3xl flex items-center justify-center mb-6 shadow-sm',
    'transform-gpu transition-[transform,background-color] duration-200 ease-out',
    'will-change-transform group-hover:scale-105',
    iconContainerClassName
  )
}

function getActionCardFooterHighlightClassName(): string {
  return cn(
    'pointer-events-none absolute inset-x-0 bottom-0 h-1.5 bg-primary/60 opacity-0',
    'transition-opacity duration-200 group-hover:opacity-100'
  )
}

const ActionCard = memo(function ActionCard({
  onClick,
  title,
  description,
  icon: Icon,
  iconClassName,
  iconContainerClassName,
  borderClassName,
  gradientClassName,
}: ActionCardProps) {
  return (
    <Card
      onClick={onClick}
      className={getActionCardClassName(borderClassName)}
    >
      <div className={getActionCardOverlayClassName(gradientClassName)} />
      <CardHeader className="items-center text-center pt-12 pb-4">
        <div className={getActionCardIconContainerClassName(iconContainerClassName)}>
          <Icon className={iconClassName ?? 'w-12 h-12'} />
        </div>
        <CardTitle className="text-3xl md:text-4xl font-black tracking-tight">{title}</CardTitle>
      </CardHeader>
      <CardContent className="text-center pb-12 px-10">
        <p className="text-muted-foreground text-xl leading-relaxed">{description}</p>
      </CardContent>
      {/* Underline indicator (keeps the existing “bottom bar” visual) */}
      <div className={getActionCardFooterHighlightClassName()} />
    </Card>
  )
})

export const Route = createFileRoute('/')({
  beforeLoad: ({ context, location }) => {
    if (shouldRedirectToLogin(context, location.pathname)) {
      throw redirect({ to: '/login', replace: true })
    }
  },
  component: IndexPage,
})

function shouldRedirectToLogin(context: RouterContext, pathname: string): boolean {
  if (pathname !== '/') return false
  const isAuthenticated = selectIsAuthenticated(context.store.getState())
  return !isAuthenticated
}

function IndexPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [step, setStep] = useState<HomeStep>('choose')

  const handleScanClick = useCallback(() => {
    navigate({ to: '/scan' })
  }, [navigate])

  const handleCustomerPortalClick = useCallback(() => {
    const portalUrl = getPortalUrl('customer')
    window.open(portalUrl, '_blank')
  }, [])

  const handleEcommerceClick = useCallback(() => {
    const portalUrl = getPortalUrl('ecommerce')
    window.open(portalUrl, '_blank')
  }, [])

  const handleExistingCustomerClick = useCallback(() => {
    setStep('existing')
  }, [])

  const handleNewCustomerClick = useCallback(() => {
    handleEcommerceClick()
  }, [handleEcommerceClick])

  const handleInboundDeliveryClick = useCallback(() => {
    navigate({ to: '/inbound' })
  }, [navigate])

  const handleBackToOptions = useCallback(() => {
    setStep('choose')
  }, [])

  return (
    <div className="min-h-screen flex flex-col items-center p-4 md:p-8 lg:p-12 overflow-x-hidden">

      {/* Header / Hero Section */}
      <header className="w-full max-w-7xl mx-auto flex flex-col items-center text-center mb-16">
         <div className="bg-primary/10 p-5 rounded-3xl mb-8 ring-8 ring-primary/5 shadow-inner cursor-default">
           <LayoutGrid className="h-16 w-16 text-primary" />
         </div>
         <h1 className="text-5xl md:text-7xl font-black text-foreground mb-6 tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/70">
           {t('home.welcome', 'Welcome to South Gate')}
         </h1>
         <p className="text-xl md:text-3xl text-muted-foreground max-w-3xl leading-relaxed">
           {t('home.subtitle', 'Please select an option below to proceed with your operation.')}
         </p>
      </header>

      {/* Main Grid */}
      <main className="w-full max-w-7xl mx-auto flex-grow content-start pb-20">
        {step === 'choose' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <ActionCard
              onClick={handleExistingCustomerClick}
              title={t('home.existingCustomer', 'Existing Customer')}
              description={t('home.existingCustomerDesc', 'Scan Order or Purchase using your account.')}
              icon={LayoutGrid}
              iconClassName="w-12 h-12 text-blue-600 dark:text-blue-400"
              iconContainerClassName="bg-blue-500/10 group-hover:bg-blue-500/20"
              borderClassName="border-primary/10"
              gradientClassName="bg-gradient-to-br from-blue-500/10 via-transparent to-transparent"
            />
            <ActionCard
              onClick={handleNewCustomerClick}
              title={t('home.newCustomer', 'New Customer')}
              description={t('home.newCustomerDesc', 'Open ecommerce, then continue.')}
              icon={ShoppingCart}
              iconClassName="w-12 h-12 text-orange-600 dark:text-orange-400"
              iconContainerClassName="bg-orange-500/10 group-hover:bg-orange-500/20"
              borderClassName="border-primary/10"
              gradientClassName="bg-gradient-to-br from-orange-500/10 via-transparent to-transparent"
            />
            <ActionCard
              onClick={handleInboundDeliveryClick}
              title={t('home.inboundDelivery', 'Delivering to Us')}
              description={t(
                'home.inboundDeliveryDesc',
                'Register your delivery if the gate could not read your QR or plate.',
              )}
              icon={Truck}
              iconClassName="w-12 h-12 text-purple-600 dark:text-purple-400"
              iconContainerClassName="bg-purple-500/10 group-hover:bg-purple-500/20"
              borderClassName="border-primary/10"
              gradientClassName="bg-gradient-to-br from-purple-500/10 via-transparent to-transparent"
            />
          </div>
        )}

        {step === 'existing' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              <ActionCard
                onClick={handleScanClick}
                title={t('home.scan', 'Scan Order')}
                description={t('home.scanDesc', 'Scan your QR code or enter order number to begin weighing.')}
                icon={Scan}
                iconClassName="w-12 h-12 text-blue-600 dark:text-blue-400"
                iconContainerClassName="bg-blue-500/10 group-hover:bg-blue-500/20"
                borderClassName="border-primary/10"
                gradientClassName="bg-gradient-to-br from-blue-500/10 via-transparent to-transparent"
              />
              <ActionCard
                onClick={handleCustomerPortalClick}
                title={t('header.customerPortal', 'Purchase')}
                description={t('home.purchaseDesc', 'Create new orders or manage your account via Customer Portal.')}
                icon={LayoutGrid}
                iconClassName="w-12 h-12 text-green-600 dark:text-green-400"
                iconContainerClassName="bg-green-500/10 group-hover:bg-green-500/20"
                borderClassName="border-primary/10"
                gradientClassName="bg-gradient-to-br from-green-500/10 via-transparent to-transparent"
              />
            </div>

            <div className="mt-16 flex justify-center pb-12">
              <div className="flex w-full max-w-[1400px] flex-col items-center justify-center gap-4 sm:flex-row">
                <Button
                  onClick={handleBackToOptions}
                  variant="outline"
                  data-testid="kiosk-back-home"
                  className="h-20 px-12 text-2xl font-black gap-4 shadow-lg hover:scale-[1.02] transition-[transform,box-shadow] rounded-3xl border-4"
                >
                  <ArrowLeft className="h-8 w-8" />
                  {t('common.backToHome', 'Back to Home')}
                </Button>
              </div>
            </div>
          </>
        )}

      </main>

    </div>
  )
}
